/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import TelescopeIcon from '@atlaskit/icon-lab/core/telescope';
import CrossIcon from '@atlaskit/icon/core/cross';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { messages } from './messages';

const tagStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	gap: token('space.050'),
	paddingInlineStart: token('space.075'),
	paddingInlineEnd: token('space.050'),
	paddingBlock: token('space.050'),
	borderRadius: token('radius.small'),
	backgroundColor: token('color.background.neutral'),
	borderWidth: token('border.width'),
	borderStyle: 'solid',
	borderColor: token('color.border'),
	color: token('color.text.subtle'),
	font: token('font.body.small'),
	whiteSpace: 'nowrap',
});

const dismissButtonStyles = css({
	display: 'flex',
	alignItems: 'center',
	paddingTop: token('space.0'),
	paddingRight: token('space.0'),
	paddingBottom: token('space.0'),
	paddingLeft: token('space.0'),
	background: 'none',
	border: 'none',
	cursor: 'pointer',
	color: token('color.text.subtle'),
	borderRadius: token('radius.small'),
	'&:disabled': {
		cursor: 'default',
	},
});

type DeepResearchTagProps = {
	onDismiss: () => void;
	isDisabled?: boolean;
	disabledTooltip?: string;
};

export const DeepResearchTag = ({
	onDismiss,
	isDisabled,
	disabledTooltip,
}: DeepResearchTagProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const tag = (
		<span css={tagStyles}>
			<TelescopeIcon label="" size="small" />
			{formatMessage(messages.deepResearchTagLabel)}
			<button
				css={dismissButtonStyles}
				onClick={onDismiss}
				aria-label={formatMessage(messages.deepResearchTagDismissLabel)}
				disabled={isDisabled}
				type="button"
			>
				<CrossIcon label="" size="small" />
			</button>
		</span>
	);

	return disabledTooltip ? <Tooltip content={disabledTooltip}>{tag}</Tooltip> : tag;
};
